import React, { useMemo } from 'react';
import { Alert, Box, CircularProgress, IconButton } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { AthleteActivityDayCard } from './AthleteActivityDayCard';
import { ReviewSuspiciousHandler } from './AthleteActivityIntegrity';
import { AthleteActivityItem, computeStats, formatPace, groupActivitiesByDay } from './athleteActivityStats';

interface AthleteActivityPanelProps {
  name: string;
  // e.g. "#3 · Team Alpha · October 100K"; callers omit unknown parts.
  subtitle?: string;
  activities: AthleteActivityItem[];
  loading?: boolean;
  error?: string | null;
  onClose?: () => void;
  onBack?: () => void;
  // Drawer fills its container and scrolls the body; inline just flows in the page.
  fill?: boolean;
  // Admin action on a flagged activity; callers refetch afterwards.
  onReviewSuspicious?: ReviewSuspiciousHandler;
}

const StatCell = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ backgroundColor: 'var(--card)', p: '12px 14px', display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
    <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)' }}>{label}</Box>
    <Box component="span" sx={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
  </Box>
);

// Athlete header + 2x2 stats + activities grouped by day.
export const AthleteActivityPanel = ({ name, subtitle, activities, loading, error, onClose, onBack, fill, onReviewSuspicious }: AthleteActivityPanelProps) => {
  const { t, i18n } = useTranslation('contests');
  const stats = useMemo(() => computeStats(activities), [activities]);
  // Day labels are localized, so regroup when the language changes.
  const days = useMemo(() => groupActivitiesByDay(activities), [activities, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', color: 'var(--fg)', ...(fill ? { height: '100%', minHeight: 0 } : {}) }}>
      <Box sx={{ p: '18px 20px', display: 'flex', gap: 1.5, alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        {onBack && (
          <IconButton aria-label={t('common:actions.back')} onClick={onBack} size="small" sx={{ color: 'var(--muted)', ml: -0.75 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <InitialsAvatar name={name} size={44} />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <Box component="span" sx={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</Box>
          {subtitle && <Box component="span" sx={{ fontSize: 13, color: 'var(--muted)' }}>{subtitle}</Box>}
        </Box>
        {onClose && (
          <IconButton aria-label={t('common:actions.close')} onClick={onClose} size="small" sx={{ color: 'var(--muted)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box sx={{ p: '18px 20px 28px', display: 'grid', alignContent: 'start', gridAutoRows: 'max-content', gap: '18px', ...(fill ? { flex: 1, minHeight: 0, overflow: 'auto' } : {}) }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1px', backgroundColor: 'var(--border)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
              <StatCell label={t('athlete.stats.countedDistance')} value={t('common:units.km', { value: stats.totalDistance.toFixed(1) })} />
              <StatCell label={t('athlete.stats.activeDays')} value={String(stats.activeDays)} />
              <StatCell label={t('athlete.stats.avgPace')} value={formatPace(stats.averagePace)} />
              <StatCell label={t('athlete.stats.bestPace')} value={formatPace(stats.bestPace)} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Box component="span" sx={{ fontSize: 15, fontWeight: 600 }}>{t('athlete.activitiesByDay')}</Box>
              <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)' }}>{t('athlete.dayCount', { count: days.length })}</Box>
            </Box>
            {days.length === 0 ? (
              <Box sx={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', py: 3 }}>{t('athlete.empty')}</Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {days.map(d => <AthleteActivityDayCard key={d.key} day={d} onReviewSuspicious={onReviewSuspicious} />)}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
